/**
 * OAuth Constants for Sphinx
 */

/** The deployed sphinx origin (Render). */
export const SPHINX_HOSTED_APP_ORIGIN = "https://sphinx.yarn.so";

/**
 * Path of sphinx's Client ID Metadata Document
 * (draft-parecki-oauth-client-id-metadata-document). The server serves it on
 * its own origin; the document lists every redirect URI sphinx uses
 * (`<origin>/oauth/callback` plus the localhost dev origins).
 */
export const CLIENT_ID_METADATA_PATH = "/.well-known/oauth/client-metadata.json";

/**
 * Client ID Metadata Document URL presented as `client_id` to authorization
 * servers that support CIMD (Yarn's AS does, and it has no dynamic client
 * registration). A CIMD `client_id` must be an https URL on a public host, so
 * the document on the current origin is used only when that origin is https;
 * plain-http dev origins (`http://localhost:5173`) present the hosted
 * document, which lists the localhost redirect URIs (loopback redirect URIs
 * are allowed on any port per RFC 8252 §7.3).
 */
export function resolveClientIdMetadataUrl(
  locationLike: Pick<Location, "protocol" | "origin">
): string {
  const origin =
    locationLike.protocol === "https:"
      ? locationLike.origin
      : SPHINX_HOSTED_APP_ORIGIN;
  return `${origin}${CLIENT_ID_METADATA_PATH}`;
}

export function getClientIdMetadataUrl(): string {
  if (typeof window !== "undefined") {
    return resolveClientIdMetadataUrl(window.location);
  }
  return `${SPHINX_HOSTED_APP_ORIGIN}${CLIENT_ID_METADATA_PATH}`;
}

/**
 * The browser always receives its OWN OAuth callback: the pending marker, the
 * resume record and the stored flow state are all per-origin, so a callback
 * landing on a different host could not finish the authorization.
 */
export function resolveBrowserOAuthRedirectOrigin(
  locationLike: Pick<Location, "protocol" | "origin" | "hostname">
): string {
  if (locationLike.protocol !== "http:" && locationLike.protocol !== "https:") {
    // Defensive fallback for non-browser-like locations. Electron exits earlier.
    return SPHINX_HOSTED_APP_ORIGIN;
  }
  return locationLike.origin;
}

export function getRedirectUri(): string {
  if (typeof window !== "undefined") {
    return `${resolveBrowserOAuthRedirectOrigin(
      window.location
    )}/oauth/callback`;
  }

  // Default fallback
  return "http://localhost:6274/oauth/callback";
}
